import React, { useEffect, useState } from "react";
import { Badge, Button, Card, Form, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import IsoDatePicker from "../../../components/IsoDatePicker";
import {
  WEEK_DAYS,
  deleteEmergencyContact,
  formatClock,
  listEmergencyContacts,
  saveEmergencyContact,
} from "../../../services/emergencyContactApi";
import { formatDateDMY } from "../../../utils/dateFormat";

const blankSlot = () => ({ days: [], startTime: "08:00", endTime: "20:00", fromDate: "", toDate: "" });

const blank = () => ({
  name: "",
  role: "",
  phone: "",
  whatsapp: true,
  languages: "",
  displayOrder: 0,
  active: true,
  slots: [blankSlot()],
});

// The people customers can call during a trip. The customer trip page shows each one only while
// one of their available times is on, and only around the trip.
export default function EmergencyContactsManager() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(blank);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listEmergencyContacts();
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load emergency contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const patch = (field, value) => setEditing((prev) => ({ ...prev, [field]: value }));
  const patchSlot = (index, field, value) => setEditing((prev) => ({
    ...prev,
    slots: prev.slots.map((slot, i) => (i === index ? { ...slot, [field]: value } : slot)),
  }));
  const toggleDay = (index, day) => setEditing((prev) => ({
    ...prev,
    slots: prev.slots.map((slot, i) => {
      if (i !== index) return slot;
      const days = slot.days.includes(day) ? slot.days.filter((value) => value !== day) : [...slot.days, day];
      return { ...slot, days };
    }),
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = normalize(editing);
      await saveEmergencyContact(payload);
      setEditing(blank());
      toast.success("Emergency contact saved");
      load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not save emergency contact");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.name}?`)) return;
    try {
      await deleteEmergencyContact(row.id);
      setRows((prev) => prev.filter((item) => item.id !== row.id));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not delete emergency contact");
    }
  };

  const edit = (row) => setEditing({
    ...row,
    role: row.role || "",
    languages: row.languages || "",
    slots: (row.slots || []).map((slot) => ({
      days: slot.days || [],
      startTime: String(slot.startTime || "").slice(0, 5),
      endTime: String(slot.endTime || "").slice(0, 5),
      fromDate: slot.fromDate || "",
      toDate: slot.toDate || "",
    })),
  });

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <div className="fw-semibold">Emergency Contacts</div>
        <div className="text-muted small">
          Shown on the customer trip page from the day before the trip to its last day, after the policy is accepted -
          and only while the contact is available. When nobody is, the page says when someone will be.
        </div>
      </Card.Header>
      <Card.Body>
        <Card className="mb-3">
          <Card.Body>
            <div className="fw-semibold mb-3">{editing.id ? "Edit Emergency Contact" : "New Emergency Contact"}</div>
            <div className="row g-3">
              <div className="col-md-3">
                <Form.Label>Name</Form.Label>
                <Form.Control value={editing.name} onChange={(e) => patch("name", e.target.value)} />
              </div>
              <div className="col-md-3">
                <Form.Label>Role</Form.Label>
                <Form.Control value={editing.role} onChange={(e) => patch("role", e.target.value)} placeholder="e.g. Trip Coordinator" />
              </div>
              <div className="col-md-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control type="tel" value={editing.phone} onChange={(e) => patch("phone", e.target.value)} />
                <Form.Check
                  type="checkbox"
                  className="small mt-1"
                  id="emergency-whatsapp"
                  label="Also on WhatsApp"
                  checked={editing.whatsapp !== false}
                  onChange={(e) => patch("whatsapp", e.target.checked)}
                />
              </div>
              <div className="col-md-3">
                <Form.Label>Languages</Form.Label>
                <Form.Control value={editing.languages} onChange={(e) => patch("languages", e.target.value)} placeholder="e.g. Kannada, English" />
              </div>
              <div className="col-md-2">
                <Form.Label>Order</Form.Label>
                <Form.Control type="number" value={editing.displayOrder || 0} onChange={(e) => patch("displayOrder", Number(e.target.value || 0))} />
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <Form.Check type="switch" label="Active" checked={editing.active !== false} onChange={(e) => patch("active", e.target.checked)} />
              </div>
            </div>

            <div className="fw-semibold mt-4 mb-1">Available Times</div>
            <div className="small text-muted mb-2">
              No day ticked means every day. An end time earlier than the start runs past midnight (8:00 PM to 8:00 AM is a night shift).
              Dates are optional - leave them empty for every week.
            </div>
            {editing.slots.map((slot, index) => (
              <div key={index} className="border rounded p-2 mb-2">
                <div className="d-flex flex-wrap gap-1 mb-2">
                  {WEEK_DAYS.map(([code, label]) => (
                    <Button
                      key={code}
                      size="sm"
                      variant={slot.days.includes(code) ? "primary" : "outline-secondary"}
                      onClick={() => toggleDay(index, code)}
                    >
                      {label}
                    </Button>
                  ))}
                  <span className="small text-muted align-self-center ms-1">
                    {slot.days.length ? "" : "Every day"}
                  </span>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-end">
                  <div>
                    <Form.Label className="small mb-1">From time</Form.Label>
                    <Form.Control size="sm" type="time" value={slot.startTime} onChange={(e) => patchSlot(index, "startTime", e.target.value)} />
                  </div>
                  <div>
                    <Form.Label className="small mb-1">To time</Form.Label>
                    <Form.Control size="sm" type="time" value={slot.endTime} onChange={(e) => patchSlot(index, "endTime", e.target.value)} />
                  </div>
                  <div style={{ width: 140 }}>
                    <Form.Label className="small mb-1">From date</Form.Label>
                    <IsoDatePicker value={slot.fromDate} onChange={(value) => patchSlot(index, "fromDate", value)} className="form-control form-control-sm" placeholderText="Any" />
                  </div>
                  <div style={{ width: 140 }}>
                    <Form.Label className="small mb-1">To date</Form.Label>
                    <IsoDatePicker value={slot.toDate} onChange={(value) => patchSlot(index, "toDate", value)} className="form-control form-control-sm" placeholderText="Any" />
                  </div>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={editing.slots.length === 1}
                    onClick={() => patch("slots", editing.slots.filter((_, i) => i !== index))}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
            <Button size="sm" variant="outline-primary" onClick={() => patch("slots", [...editing.slots, blankSlot()])}>
              Add Available Time
            </Button>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button variant="outline-secondary" onClick={() => setEditing(blank())}>Clear</Button>
              <Button disabled={saving || !editing.name.trim() || !editing.phone.trim()} onClick={save}>
                {saving ? "Saving..." : "Save Contact"}
              </Button>
            </div>
          </Card.Body>
        </Card>

        {loading ? (
          <div className="py-5 text-center"><Spinner /></div>
        ) : (
          <Table responsive hover size="sm" className="align-middle">
            <thead>
              <tr><th style={{ width: 44 }}>#</th>
                <th>Contact</th>
                <th>Phone</th>
                <th>Available Times</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!rows.length && (
                <tr><td colSpan={6} className="text-muted text-center py-3">No emergency contacts yet.</td></tr>
              )}
              {rows.map((row, index) => (
                <tr key={row.id}><td className="small fw-semibold">{index + 1}</td>
                  <td>
                    <div className="fw-semibold">{row.name}</div>
                    <div className="small text-muted">{[row.role, row.languages].filter(Boolean).join(" · ")}</div>
                  </td>
                  <td className="text-nowrap">
                    {row.phone}
                    {row.whatsapp ? <div className="small text-muted">WhatsApp</div> : null}
                  </td>
                  <td className="small">
                    {(row.slots || []).map((slot, slotIndex) => <div key={slotIndex}>{slotSummary(slot)}</div>)}
                  </td>
                  <td>
                    {!row.active ? <Badge bg="secondary">Inactive</Badge>
                      : row.availableNow ? <Badge bg="success">Available now</Badge>
                        : <Badge bg="light" text="dark">Not available now</Badge>}
                  </td>
                  <td className="text-end text-nowrap">
                    <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => edit(row)}>Edit</Button>
                    <Button size="sm" variant="outline-danger" onClick={() => remove(row)}>Delete</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
}

function slotSummary(slot) {
  const days = slot.days?.length
    ? WEEK_DAYS.filter(([code]) => slot.days.includes(code)).map(([, label]) => label).join(", ")
    : "Every day";
  const start = String(slot.startTime || "").slice(0, 5);
  const end = String(slot.endTime || "").slice(0, 5);
  const overnight = end <= start ? " (next day)" : "";
  const dates = slot.fromDate || slot.toDate
    ? ` · ${slot.fromDate ? formatDateDMY(slot.fromDate, "") : "any"} to ${slot.toDate ? formatDateDMY(slot.toDate, "") : "any"}`
    : "";
  return `${days} · ${formatClock(start)} to ${formatClock(end)}${overnight}${dates}`;
}

function normalize(row) {
  return {
    id: row.id,
    name: String(row.name || "").trim(),
    role: String(row.role || "").trim(),
    phone: String(row.phone || "").trim(),
    whatsapp: row.whatsapp !== false,
    languages: String(row.languages || "").trim(),
    displayOrder: Number(row.displayOrder || 0),
    active: row.active !== false,
    slots: (row.slots || []).map((slot) => ({
      days: slot.days || [],
      startTime: slot.startTime,
      endTime: slot.endTime,
      fromDate: slot.fromDate || null,
      toDate: slot.toDate || null,
    })),
  };
}
